import { Box, Typography } from '@mui/material';
import { Hospital } from '_api/types';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddHospital, EditHospital } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useHospitals, useSearchResults } from 'utils/hooks';

export const HospitalManagement = () => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const { activeHospitals, deleteHospital, isLoading } = useHospitals();
  const [openModal, setModalOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editHospitalId, setEditHospitalId] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (editHospitalId > 0) setOpenEdit(true);
  }, [editHospitalId]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  const filteredData = useMemo(() => {
    if (!activeHospitals) return [];
    if (searchTerm === '') return activeHospitals;
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return activeHospitals.filter((hospital) => {
      const title = hospital.name.toLowerCase();
      const streetAddress = hospital.streetAddress.toLowerCase();
      const state = hospital.state.toLowerCase();
      const city = hospital.city.toLowerCase();
      return (
        title.includes(lowercasedSearchTerm) ||
        streetAddress.includes(lowercasedSearchTerm) ||
        city.includes(lowercasedSearchTerm) ||
        state.includes(lowercasedSearchTerm)
      );
    });
  }, [searchTerm, activeHospitals]);

  const handleOpenEditModal = (id: number) => {
    setEditHospitalId(id);
  };
  const handleCloseEdit = () => {
    setEditHospitalId(0);
    setOpenEdit(false);
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const areaCode = phoneNumber.slice(0, 3);
    const firstThree = phoneNumber.slice(3, 6);
    const lastFour = phoneNumber.slice(6);
    return `(${areaCode})${firstThree}-${lastFour}`;
  };

  const actionButtons: ViewButtons = {
    editButton: true,
    archiveButton: true,
  };

  const handleArchiveOnClick = (hospitalId: number) => {
    deleteHospital({ hospitalId });
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  const tableColumns = useMemo<MRT_ColumnDef<Hospital>[]>(
    () => [
      {
        header: 'Name',
        accessorFn: (row) => `${row.name}`,
      },
      {
        header: 'Address',
        enableSorting: false,
        accessorFn: (row) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
            <Typography>{row.streetAddress}</Typography>
            <Typography>{`${row.city}, ${row.state} ${row.zipCode}`}</Typography>
          </Box>
        ),
      },
      {
        header: 'Phone Number',
        enableSorting: false,
        accessorFn: (row) =>
          row && row.phoneNumber ? formatPhoneNumber(row.phoneNumber) : '',
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'hospital/clinic'}
            archiveOnClick={() => handleArchiveOnClick(row.id)}
            editOnClick={() => handleOpenEditModal(row.id)}
            actionButtons={actionButtons}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          onAddClick={() => setModalOpen(true)}
          handleSearch={handleSearch}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddHospital open={openModal} onClose={() => setModalOpen(false)} />
      {editHospitalId > 0 ? (
        <EditHospital
          hospitalId={editHospitalId}
          open={openEdit}
          onClose={handleCloseEdit}
        />
      ) : null}
    </Box>
  );
};
